import React, { useState } from "react";
import { ChevronRight, Users, UserCheck, Home, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SPONSOR_STATUS_OPTIONS = [
  { id: "yes", label: "Yes, I have a sponsor", icon: "✅", description: "Currently working with a sponsor" },
  { id: "looking", label: "Looking for a sponsor", icon: "🔍", description: "Open to finding one" },
  { id: "no", label: "No, and not looking", icon: "❌", description: "Working my program differently" },
  { id: "prefer_not_to_say", label: "Prefer not to say", icon: "🔒", description: "Private" },
];

const SPONSORING_STATUS_OPTIONS = [
  { id: "yes", label: "Yes, I sponsor others", icon: "🤝", description: "Giving back to the community" },
  { id: "open_to_it", label: "Open to sponsoring", icon: "💭", description: "Ready when the time is right" },
  { id: "not_yet", label: "Not yet", icon: "⏳", description: "Still growing in my recovery" },
  { id: "prefer_not_to_say", label: "Prefer not to say", icon: "🔒", description: "Private" },
];

const HOME_GROUP_OPTIONS = [
  { id: "yes", label: "Yes, I have a home group", description: "Regular meeting I attend" },
  { id: "multiple", label: "Multiple home groups", description: "Active in several groups" },
  { id: "looking", label: "Looking for one", description: "Still finding my fit" },
  { id: "no", label: "No home group", description: "Not currently affiliated" },
];

const PARTNER_SPONSOR_IMPORTANCE = [
  { id: "very_important", label: "Very Important", description: "I strongly prefer a partner who works with a sponsor" },
  { id: "somewhat_important", label: "Somewhat Important", description: "It's a plus but not required" },
  { id: "not_important", label: "Not Important", description: "Their recovery path is their own" },
  { id: "no_preference", label: "No Preference", description: "Open to any approach" },
];

const SponsorSupportStep = ({ onNext }) => {
  const [hasSponsor, setHasSponsor] = useState("");
  const [isSponsor, setIsSponsor] = useState("");
  const [homeGroup, setHomeGroup] = useState("");
  const [partnerSponsorImportance, setPartnerSponsorImportance] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!hasSponsor) {
      toast.error("Please select your sponsor status");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.submitSponsorSupport({
        has_sponsor: hasSponsor,
        is_sponsor: isSponsor || "prefer_not_to_say",
        home_group: homeGroup || "no",
        partner_sponsor_importance: partnerSponsorImportance || "no_preference"
      });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-6 h-6 text-[#FAB710]" />
        <h2 className="text-xl font-bold text-gray-900">Sponsor & Support</h2>
      </div>
      <p className="text-gray-600 mb-4">Your support system is key to recovery</p>

      <div className="bg-blue-50 rounded-xl p-4 mb-6">
        <p className="text-sm text-blue-700">
          <strong>Why this matters:</strong> Understanding each other's recovery support helps build stronger, more compatible connections.
        </p>
      </div>

      {/* Do you have a sponsor? */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <UserCheck className="w-4 h-4" />
          Do you have a sponsor?
        </label>
        <div className="space-y-2">
          {SPONSOR_STATUS_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setHasSponsor(option.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                hasSponsor === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{option.icon}</span>
                <div>
                  <p className={`font-medium ${hasSponsor === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                    {option.label}
                  </p>
                  <p className="text-gray-500 text-sm">{option.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Are you a sponsor? */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Heart className="w-4 h-4" />
          Do you sponsor others?
        </label>
        <div className="grid grid-cols-2 gap-2">
          {SPONSORING_STATUS_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setIsSponsor(option.id)}
              className={`px-3 py-3 rounded-xl border-2 text-left transition-all ${
                isSponsor === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-lg block mb-1">{option.icon}</span>
              <p className={`font-medium text-sm ${isSponsor === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
              <p className="text-gray-500 text-xs">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Home Group */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Home className="w-4 h-4" />
          Do you have a home group?
        </label>
        <div className="grid grid-cols-2 gap-2">
          {HOME_GROUP_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setHomeGroup(option.id)}
              className={`px-3 py-3 rounded-xl border-2 text-left transition-all ${
                homeGroup === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium text-sm ${homeGroup === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
              <p className="text-gray-500 text-xs">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Partner Sponsor Preference */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How important is it that your partner works with a sponsor?
        </label>
        <div className="space-y-2">
          {PARTNER_SPONSOR_IMPORTANCE.map(option => (
            <button
              key={option.id}
              onClick={() => setPartnerSponsorImportance(option.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                partnerSponsorImportance === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium ${partnerSponsorImportance === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
              <p className="text-gray-500 text-sm">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || !hasSponsor}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default SponsorSupportStep;
